import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, BookOpen } from "lucide-react";
import { listArticles, listCategories, resolveImage } from "@/lib/blogApi";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd } from "@/data/seo";

const RED = "#D41F30";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogIndexPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listArticles(page, category), listCategories()])
      .then(([list, cats]) => {
        if (cancelled) return;
        setArticles((prev) => (page === 1 ? list.items : [...prev, ...list.items]));
        setHasMore(list.hasMore);
        setCategories(cats);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [page, category]);

  const onCategory = (c) => {
    setCategory(c);
    setPage(1);
    setArticles([]);
  };

  const [hero, ...rest] = articles;

  return (
    <div className="bg-white min-h-screen" data-testid="blog-index-page">
      <SEO
        title="Blog | Homebuying, Mortgage & Real Estate Insights | CityWorth"
        description="Plain-English guides on homebuying, mortgage programs, credit, and real estate. Written by licensed CityWorth mortgage advisors."
        path="/blog"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "CityWorth Blog",
            description: "Plain-English guides on homebuying, mortgage, and real estate.",
            path: "/blog",
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ])
        )}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden bg-white">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
            <BookOpen className="w-4 h-4" style={{ color: RED }} />
            <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
              CityWorth Blog
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6 max-w-3xl">
            Homebuying, <span style={{ color: RED }}>decoded.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl">
            Plain-English guides on mortgages, credit, real estate, and everything in between. Written
            by the licensed CityWorth team.
          </p>

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
              <button
                onClick={() => onCategory(null)}
                data-testid="cat-all"
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  category === null
                    ? "bg-[#D41F30] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.category}
                  data-testid={`cat-${c.category.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => onCategory(c.category)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                    category === c.category
                      ? "bg-[#D41F30] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {c.category} <span className="opacity-60">({c.count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Articles */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading && articles.length === 0 ? (
            <div className="text-center py-24 text-gray-500" data-testid="blog-loading">
              Loading articles...
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-24" data-testid="blog-empty">
              <p className="text-xl text-gray-500 mb-4">No articles yet.</p>
              <p className="text-sm text-gray-400">
                Our team is drafting fresh content daily. Check back tomorrow.
              </p>
            </div>
          ) : (
            <>
              {/* Hero card */}
              {hero && (
                <Link
                  to={`/blog/${hero.slug}`}
                  className="group block rounded-3xl overflow-hidden mb-12 bg-gray-50 hover:shadow-xl transition-all duration-300"
                  data-testid={`blog-hero-${hero.slug}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0">
                    <div className="aspect-[16/10] lg:aspect-auto overflow-hidden">
                      {hero.image_url && (
                        <img
                          src={resolveImage(hero.image_url)}
                          alt={hero.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      )}
                    </div>
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-3 text-xs font-bold tracking-wider uppercase mb-4" style={{ color: RED }}>
                        <span>{hero.category}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {hero.reading_time} min read
                        </span>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 leading-[1.1] mb-4 group-hover:text-[#D41F30] transition-colors">
                        {hero.title}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                        {hero.meta_description}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: RED }}>
                        Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((a) => (
                    <Link
                      key={a.id}
                      to={`/blog/${a.slug}`}
                      className="group block bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300"
                      data-testid={`blog-card-${a.slug}`}
                    >
                      {a.image_url && (
                        <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                          <img
                            src={resolveImage(a.image_url)}
                            alt={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase mb-3" style={{ color: RED }}>
                          <span>{a.category}</span>
                          <span className="text-gray-300">·</span>
                          <span className="text-gray-500 font-medium normal-case tracking-normal flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {a.reading_time} min
                          </span>
                        </div>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-gray-900 leading-tight mb-3 group-hover:text-[#D41F30] transition-colors">
                          {a.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                          {a.meta_description}
                        </p>
                        {a.published_at && (
                          <p className="mt-4 text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(a.published_at)}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {hasMore && (
                <div className="text-center mt-16">
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white border-2 border-[#D41F30] hover:border-[#B91C2A] rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    data-testid="blog-load-more"
                  >
                    Load more <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
